import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { CSVLink } from "react-csv";
import moment from "moment";
import { toast } from "react-toastify";
import { Dropdown, Row } from "react-bootstrap";
import { ExcelIcon, ExportIcon } from "../icons";
import { useDownloadExcel } from "../useDownloadExcel";
import { URLS } from "../urls";
import ModalLoader from "../ModalLoader";
import TableComponent from "../TableComponent";
import {
  arrangeEventObject,
  fetchActionsUtil,
  stringifyQueryString,
} from "../../utils";
import NoTableItem from "../NoTableItem";
import { SingleDatePicker } from "../SingleDatePicker";
import CustomPagination from "../Paginations";
import { UseQueryData } from "../UseQueryData";

export function LeaveScheduleComponent({ branch_id, SwitchCompanyComponent }) {
  // ------------------ Filter Search -------------------//
  const initialQueryParams = {
    limit: 20,
    page: 1,
    id: "",
    name: "",
    startDate: "",
    endDate: "",
  };
  const { queryParams, theQueryParams, onChangeInput, setQueryParams } =
    UseQueryData({
      initialQueryParams,
      timer: 1500,
    });

  const [returnParams, setReturnParams] = useState({
    count: 0,
    endIndex: 0,
    startIndex: 0,
  });

  // ------------------ Filter Search -------------------//

  const [payroll, setPayroll] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  //-------------- fetch excel hook
  const [excelData, setExcelData] = useState([]);
  const CSVLinkRef = useRef(null);
  const [isfetchingExcel] = useDownloadExcel(excelData, CSVLinkRef);
  //-------------- fetch excel hook

  const getLoan = useCallback(async () => {
    setIsLoading(true);

    try {
      // Parallel requests
      const [data] = await Promise.all([
        fetchActionsUtil(
          `${
            URLS.backendLeaveBalances
          }/get-leave-schedule-report?${stringifyQueryString({
            ...queryParams,
            branch_id,
          })}`
        ),
      ]);

      // Handle empty payroll
      const loan = data.leaveBalances || [];
      if (!loan.length) {
        setPayroll([]);
        return;
      }
      setReturnParams({
        count: data.count,
        endIndex: data.endIndex || 0,
        startIndex: data.startIndex || 0,
      });
      setPayroll(loan);
    } catch (err) {
      console.error("processing failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, [branch_id, queryParams]);

  useEffect(() => {
    let isMounted = true; // Track component mount status

    if (isMounted) {
      getLoan();
    }
    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, [getLoan]);

  const filterByDateRange = (event, name) => {
    onChangeInput(
      arrangeEventObject({
        name: [name],
        value: moment(event).format("MM/YYYY"),
      })
    );
  };

  const validationEndDate = (currentDate) => {
    return currentDate.isAfter(
      moment(theQueryParams?.startDate, "MM/YYYY").add(0, "months")
    );
  };

  const monthArray = useMemo(() => {
    const startDate = theQueryParams.startDate
      ? moment(theQueryParams.startDate, "MM/YYYY")
      : "";

    const endDate = theQueryParams.endDate
      ? moment(theQueryParams.endDate, "MM/YYYY")
      : "";

    const monthArray = [];
    const current = moment(startDate);
    while (current.isSameOrBefore(endDate, "month")) {
      monthArray.push({
        yearMonth: current.format("MMMM YYYY"),
        date: current.format("MM/YYYY"),
      }); // "January 2023"
      current.add(1, "month");
    }

    return monthArray;
  }, [theQueryParams]);

  const onDownloadExcelData = async () => {
    if (!payroll?.length > 0) return toast.error("Nothing to download!!!");

    const normalCell = (val) => parseFloat(val.toFixed(1)) || 0;

    let exData = payroll.map(
      (
        {
          name,
          employeeID,
          accountNumber,
          totalLeaveRemaining,
          monthlyLeaveSummary,
        },
        index
      ) => {
        let balance = totalLeaveRemaining || 0; // Start with total remaining

        return [
          index + 1,
          name,
          employeeID,
          accountNumber,
          normalCell(totalLeaveRemaining || 0),
          ...monthArray
            .map(({ date }, i) => {
              const data =
                monthlyLeaveSummary?.find((s) => s.date === date) || {};
              const daysTaken = data.totalDaysTaken || 0;
              // Subtract days taken from running balance
              balance = balance - daysTaken;
              return [normalCell(daysTaken || 0), normalCell(balance || 0)];
            })
            .flat(),
        ];
      }
    );

    const normalHead = [
      "No",
      "Employee Name",
      "Employee ID",
      "Account Number",
      "Leave Credit",
      ...monthArray
        ?.map((el, i) => [`${el.yearMonth}`, `Balance ${el.yearMonth}`])
        .flat(),
    ];

    const date = `Date Prepared: ${moment(new Date()).format(
      "MMMM DD yyyy k:mm:ss z"
    )}`;

    exData = [
      ["Leave Schedule Report"],
      [date],
      [""],
      normalHead,
      ...exData,
      [""],
    ];

    // console.log(exData);
    setExcelData(exData);
  };

  // Table head function
  const tableHead = () => {
    if (!payroll?.length) return null;
    return (
      <thead className="position-sticky top-0">
        <tr>
          <th>No</th>
          <th>Employee Name</th>
          <th>Employee ID</th>
          <th>Account Number</th>
          <th>Leave Credit</th>
          {monthArray.length > 0
            ? monthArray?.map((el, i) => (
                <>
                  <th key={`custom-${i}`}>{`${el.yearMonth}`}</th>
                  <th key={`custom-${i + 1}`}>{`Balance ${el.yearMonth}`}</th>
                </>
              ))
            : null}
        </tr>
      </thead>
    );
  };

  // Table body data function
  const tableBodyData = (employee, index) => {
    const {
      name,
      employeeID,
      accountNumber,
      totalLeaveRemaining,
      monthlyLeaveSummary,
    } = employee;

    let balance = totalLeaveRemaining || 0; // Start with total remaining

    const normalCell = (val) => <td>{parseFloat(val.toFixed(1)) || 0}</td>;

    return (
      <>
        <td>{returnParams.startIndex + index + 1}</td>
        <td>{name}</td>
        <td>{employeeID}</td>
        <td>{accountNumber}</td>
        {normalCell(totalLeaveRemaining || 0)}
        {monthArray.map(({ date }, i) => {
          const data = monthlyLeaveSummary?.find((s) => s.date === date) || {};
          const daysTaken = data.totalDaysTaken || 0;
          // Subtract days taken from running balance
          balance = balance - daysTaken;
          return (
            <React.Fragment key={i}>
              {normalCell(daysTaken || 0)}
              {normalCell(balance)}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const dropDownFunction = (qty) => {
    setQueryParams((el) => ({ ...el, limit: qty }));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-baseline my-4">
        <div className="d-flex">
          <div className="d-flex align-items-center">
            <label>Start Date</label>
            <div className="mx-2">
              <SingleDatePicker
                value={theQueryParams.startDate}
                filterByDateRange={filterByDateRange}
                name="startDate"
                dateFormat="MM/YYYY"
              />
            </div>
          </div>

          <div className="d-flex align-items-center">
            <label>End Date</label>
            <div className="mx-2">
              <SingleDatePicker
                value={theQueryParams.endDate}
                filterByDateRange={filterByDateRange}
                name="endDate"
                dateFormat="MM/YYYY"
                isValidDate={validationEndDate}
              />
            </div>
          </div>
        </div>

        <div className="d-flex">
          <div className="grid-cat-1 filter-row">
            <div className="form-group form-focus">
              <input
                type="text"
                placeholder="Employee Id"
                className="form-control floating"
                value={theQueryParams.id}
                name="id"
                onChange={(e) => onChangeInput(e)}
              />
            </div>
          </div>
          <div className="grid-cat-1 filter-row mx-2">
            <div className="form-group form-focus">
              <input
                type="text"
                placeholder="Employee name"
                className="form-control floating"
                value={theQueryParams.name}
                name="name"
                onChange={(e) => onChangeInput(e)}
              />
            </div>
          </div>
          <div className="mx-4">
            <CSVLink
              className="btn print d-none"
              filename={`Leave Schedule Report (${moment(new Date()).format(
                "DD-MMM-yyyy hh:mm:ss a"
              )}).csv`}
              data={excelData}
              ref={CSVLinkRef}
            />

            <Dropdown>
              <Dropdown.Toggle
                variant=""
                className="print"
                disabled={isfetchingExcel}
                bsPrefix=""
              >
                <span className="mx-2">Export</span>
                <ExportIcon color="#008000" />
              </Dropdown.Toggle>
              <Dropdown.Menu
                popperConfig={{
                  strategy: "fixed",
                }}
                renderOnMount
                className="text-center"
              >
                <Dropdown.Item
                  as="div"
                  onClick={onDownloadExcelData}
                  className="p-cursor"
                >
                  Excel <ExcelIcon color="#008000" />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
      {SwitchCompanyComponent ? (
        <div>
          <SwitchCompanyComponent />
        </div>
      ) : null}
      <div>
        {payroll.length ? (
          <Row className="table-height">
            <TableComponent
              responsive
              striped
              sticky
              tableHeadsFunction={tableHead}
              mainDataArray={payroll}
              tableDataRowFunction={tableBodyData}
              className="product-table text-nowrap"
            />
          </Row>
        ) : (
          <NoTableItem title="No Leave Data Found for the selected criteria" />
        )}
      </div>
      <div className="my-4">
        <CustomPagination
          setQueryParams={setQueryParams}
          startIndex={returnParams.startIndex}
          endIndex={returnParams.endIndex}
          count={returnParams.count}
          limit={queryParams.limit}
          page={queryParams.page}
          dropDownFunction={dropDownFunction}
        />
      </div>
      <ModalLoader show={isLoading} />
    </div>
  );
}
